import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { List, X } from "@phosphor-icons/react";

const links = [
  { label: "Inicio", href: "#inicio", id: "nav-inicio" },
  { label: "Manifiesto", href: "#quienes-somos", id: "nav-quienes-somos" },
  { label: "Pastor", href: "#pastor", id: "nav-pastor" },
  { label: "Horarios", href: "#horarios", id: "nav-horarios" },
  { label: "Mensajes", href: "#mensajes", id: "nav-mensajes" },
  { label: "En Vivo", href: "#en-vivo", id: "nav-en-vivo" },
  { label: "Eventos", href: "#eventos", id: "nav-eventos" },
  { label: "Ministerios", href: "#ministerios", id: "nav-ministerios" },
  { label: "Ubicación", href: "#ubicacion", id: "nav-ubicacion" },
];

const secciones = [
  { label: "Radio", href: "/radio", id: "nav-radio" },
  { label: "Librería", href: "/libreria", id: "nav-libreria" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Bloquear el scroll del fondo mientras el menú móvil está abierto
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    } else {
      document.body.style.overflow = "";
      window.__lenis?.start();
    }
    return () => {
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [open]);

  const goTo = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => {
      if (window.__lenis) {
        window.__lenis.start();
        window.__lenis.scrollTo(href, { offset: -60, duration: 1.4 });
      } else {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
      }
    }, 60);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "border-b border-white/10 bg-ink/85 backdrop-blur-md" : "border-b border-transparent bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 lg:px-12">
        <a href="#inicio" onClick={(e) => goTo(e, "#inicio")} className="flex items-center gap-3" data-testid="nav-logo">
          <img src="/logo-mark-oficial.png" alt="Logo Viento de Gracia" className="h-9 w-9 object-contain" />
          <span className="font-serif text-sm uppercase tracking-[0.3em] text-paper">
            Viento de <em className="text-fire">Gracia</em>
          </span>
        </a>
        <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-links">
          {links.slice(1).map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => goTo(e, l.href)}
              data-testid={l.id}
              className="link-line text-[10px] font-light uppercase tracking-[0.3em] text-paper/60 transition-colors duration-300 hover:text-paper"
            >
              {l.label}
            </a>
          ))}
          {secciones.map((s) => (
            <a
              key={s.href}
              href={s.href}
              data-testid={s.id}
              className="link-line text-[10px] font-light uppercase tracking-[0.3em] text-fire/90 transition-colors duration-300 hover:text-fire"
            >
              {s.label}
            </a>
          ))}
          <a
            href="#contacto"
            onClick={(e) => goTo(e, "#contacto")}
            data-testid="nav-contacto-btn"
            className="border border-paper/30 px-5 py-2 text-[10px] font-light uppercase tracking-[0.3em] text-paper transition-colors duration-300 hover:border-paper hover:bg-paper hover:text-ink"
          >
            Contacto
          </a>
        </nav>
        <div className="flex items-center gap-3 lg:hidden">
          <a
            href="#contacto"
            onClick={(e) => goTo(e, "#contacto")}
            data-testid="nav-contacto-mobile"
            className="border border-paper/30 px-4 py-2 text-[10px] font-light uppercase tracking-[0.25em] text-paper"
          >
            Contacto
          </a>
          <button
            onClick={() => setOpen(true)}
            data-testid="nav-menu-open"
            aria-label="Abrir menú"
            className="flex h-10 w-10 items-center justify-center border border-paper/30 text-paper transition-colors hover:border-fire hover:text-fire"
          >
            <List size={20} weight="light" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex flex-col bg-ink/[0.98] backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <span className="flex items-center gap-3">
                <img src="/logo-mark-oficial.png" alt="Logo Viento de Gracia" className="h-9 w-9 object-contain" />
                <span className="font-serif text-sm uppercase tracking-[0.3em] text-paper">
                  Viento de <em className="text-fire">Gracia</em>
                </span>
              </span>
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Cerrar menú"
                className="flex h-10 w-10 items-center justify-center border border-paper/30 text-paper transition-colors hover:border-fire hover:text-fire"
              >
                <X size={20} weight="light" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 overflow-y-auto px-8 pb-10" data-testid="mobile-menu-links">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => goTo(e, l.href)}
                  data-testid={`mobile-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.05 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex items-baseline gap-4 border-b border-white/10 py-3"
                >
                  <span className="font-serif text-sm text-smoke">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-3xl text-paper transition-colors group-active:text-fire">{l.label}</span>
                </motion.a>
              ))}
              {secciones.map((s, i) => (
                <motion.a
                  key={s.href}
                  href={s.href}
                  data-testid={`mobile-${s.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.05 + (links.length + i) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex items-baseline gap-4 border-b border-white/10 py-3"
                >
                  <span className="font-serif text-sm text-fire">{String(links.length + i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-3xl italic text-fire">{s.label}</span>
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
